// src/components/GoogleAuthenticatorSetup.jsx
import React, { useState, useEffect } from 'react';
import { generateTOTPSecret, generateTOTPQRCode, verifyTOTPToken, generateBackupCodes } from '../utils/totpUtils';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import './GoogleAuthenticatorSetup.css';

const GoogleAuthenticatorSetup = ({ user, onSetupComplete, onCancel }) => {
  const { t } = useTranslation();
  const [secret, setSecret] = useState('');
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verification, 3: Backup Codes
  const [setupError, setSetupError] = useState('');

  useEffect(() => {
    initializeSetup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeSetup = async () => {
    try {
      console.log('Starting TOTP setup for user:', user.email);
      
      const newSecret = generateTOTPSecret();
      console.log('Generated secret:', newSecret);
      setSecret(newSecret);
      
      try {
        const qrCode = await generateTOTPQRCode(user.email, newSecret);
        console.log('Generated QR code:', qrCode ? 'Success' : 'Failed');
        setQrCodeDataURL(qrCode);
        
        if (!qrCode) {
          console.error('QR code generation failed');
          setSetupError('QR code generation failed. Please use manual entry.');
        }
      } catch (qrError) {
        console.error('QR code generation error:', qrError);
        setSetupError('QR code generation failed. Please use manual entry.');
      }
    } catch (error) {
      console.error('Error initializing TOTP setup:', error);
      setSetupError('Failed to initialize TOTP setup. Please try again.');
      setSecret('ERROR_GENERATING_SECRET');
    }
  };

  const handleVerification = async () => {
    if (!verificationToken || verificationToken.length !== 6) {
      setVerificationError(t('totp.invalidToken'));
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const isValid = await verifyTOTPToken(verificationToken, secret);
      
      if (isValid) {
        // Generate backup codes
        const codes = generateBackupCodes();
        setBackupCodes(codes);
        setStep(3);
      } else {
        setVerificationError(t('totp.invalidToken'));
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      setVerificationError(t('totp.verificationError'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCompleteSetup = async () => {
    try {
      // Use backup wrapper for TOTP setup
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      await updateUserProfileWithBackup(user.uid, {
        totpSecret: secret,
        totpEnabled: true,
        totpBackupCodes: backupCodes,
        totpSetupDate: new Date()
      }, 'security_update', {
        field: 'totp_setup',
        setupDate: new Date().toISOString(),
        backupCodesCount: backupCodes.length
      });

      onSetupComplete();
    } catch (error) {
      console.error('Error saving TOTP setup:', error);
    }
  };

  const handleSkip = () => {
    onCancel();
  };

  const renderQRCodeStep = () => (
    <div className="totp-setup-step">
      <h2>{t('totp.setupTitle')}</h2>
      <p>{t('totp.setupInstructions')}</p>
      
      <div className="qr-code-container">
        {qrCodeDataURL ? (
          <img src={qrCodeDataURL} alt="TOTP QR Code" className="qr-code" />
        ) : (
          <div className="qr-loading">
            <p>Generating QR code...</p>
          </div>
        )}
      </div>
      
      <div className="manual-entry">
        <p>{t('totp.manualEntryLabel')}</p>
        <div className="secret-display">
          <code 
            className="secret-code clickable-code"
            onClick={() => {
              if (secret) {
                navigator.clipboard.writeText(secret);
                // Show temporary feedback
                const codeElement = document.querySelector('.clickable-code');
                const originalText = codeElement.textContent;
                codeElement.textContent = 'Copied!';
                codeElement.style.color = '#28a745';
                setTimeout(() => {
                  codeElement.textContent = originalText;
                  codeElement.style.color = '';
                }, 1500);
              }
            }}
            style={{ cursor: secret ? 'pointer' : 'default' }}
          >
            {secret || 'Generating...'}
          </code>
        </div>
        {!secret && (
          <p className="loading-text">Please wait while we generate your secret key...</p>
        )}
        {setupError && (
          <p className="error-message">{setupError}</p>
        )}
        {secret && (
          <p className="click-hint">Click the code above to copy it</p>
        )}
      </div>

      <div className="step-actions">
        <button onClick={() => setStep(2)} className="primary-button">
          {t('totp.nextStep')}
        </button>
        <button onClick={handleSkip} className="secondary-button">
          {t('totp.skip')}
        </button>
      </div>
    </div>
  );

  const renderVerificationStep = () => (
    <div className="totp-setup-step">
      <h2>{t('totp.verificationTitle')}</h2>
      <p>{t('totp.verificationInstructions')}</p>
      
      <div className="verification-input">
        <input
          type="text"
          value={verificationToken}
          onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          maxLength="6"
          className="token-input"
        />
        {verificationError && (
          <p className="error-message">{verificationError}</p>
        )}
      </div>

      <div className="step-actions">
        <button 
          onClick={handleVerification} 
          disabled={isVerifying || verificationToken.length !== 6}
          className="primary-button"
        >
          {isVerifying ? t('totp.verifying') : t('totp.verify')}
        </button>
        <button onClick={() => setStep(1)} className="secondary-button">
          {t('totp.back')}
        </button>
      </div>
    </div>
  );

  const renderBackupCodesStep = () => (
    <div className="totp-setup-step">
      <h2>{t('totp.backupCodesTitle')}</h2>
      <p>{t('totp.backupCodesInstructions')}</p>
      
      <div className="backup-codes-container">
        {backupCodes.map((code, index) => (
          <div key={index} className="backup-code">
            {code}
          </div>
        ))}
      </div>
      
      <div className="backup-codes-actions">
        <button 
          onClick={() => {
            navigator.clipboard.writeText(backupCodes.join('\n'));
            setShowBackupCodes(true);
          }}
          className="secondary-button"
        >
          {t('totp.copyCodes')}
        </button>
      </div>

      {showBackupCodes && (
        <p className="success-message">{t('totp.codesCopied')}</p>
      )}

      <div className="step-actions">
        <button onClick={handleCompleteSetup} className="primary-button">
          {t('totp.completeSetup')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="google-authenticator-setup">
      {step === 1 && renderQRCodeStep()}
      {step === 2 && renderVerificationStep()}
      {step === 3 && renderBackupCodesStep()}
    </div>
  );
};

export default GoogleAuthenticatorSetup;
